import React from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, MoreHorizontal, ChevronRight, BarChart3, Briefcase, FileText, AlertTriangle, ShoppingCart, MessageSquare, Send, Receipt, Truck, CalendarDays } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const LOOK = {
  sales_month: { Icon: BarChart3, tone: "bg-emerald-50 text-emerald-600" },
  active_jobs: { Icon: Briefcase, tone: "bg-indigo-50 text-indigo-600" },
  balances: { Icon: FileText, tone: "bg-amber-50 text-amber-600" },
  priority: { Icon: AlertTriangle, tone: "bg-rose-50 text-rose-600" },
  web_orders_week: { Icon: ShoppingCart, tone: "bg-sky-50 text-sky-600" },
  enquiries: { Icon: MessageSquare, tone: "bg-violet-50 text-violet-600" },
  quotes_sent: { Icon: Send, tone: "bg-teal-50 text-teal-600" },
  expenses: { Icon: Receipt, tone: "bg-orange-50 text-orange-600" },
  vehicles: { Icon: Truck, tone: "bg-cyan-50 text-cyan-600" },
  shifts_today: { Icon: CalendarDays, tone: "bg-fuchsia-50 text-fuchsia-600" },
};
const fmt = (k, v) => (k.money ? `£${Math.round(v || 0).toLocaleString("en-GB")}` : (v ?? 0).toLocaleString("en-GB"));

function Spark({ vals }) {
  if (!vals || vals.length < 2) { return null; }
  const min = Math.min(...vals), span = Math.max(...vals) - min || 1;
  const d = vals.map((v, i) => `${i ? "L" : "M"}${((i / (vals.length - 1)) * 100).toFixed(1)},${(26 - ((v - min) / span) * 22).toFixed(1)}`).join(" ");
  return <svg viewBox="0 0 100 28" className="w-20 h-7" preserveAspectRatio="none" aria-hidden><path d={d} fill="none" stroke="#10b981" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" /></svg>;
}

function Delta({ k }) {
  const v = k.delta_pct ?? k.delta;
  if (v === null || v === undefined) { return null; }
  const up = v >= 0;
  const tone = !k.money ? "text-slate-500" : up ? "text-emerald-600" : "text-rose-600";
  const Arrow = up ? ArrowUp : ArrowDown;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${tone}`}><Arrow className="w-3 h-3" />{Math.abs(v)}{k.delta_pct !== undefined && k.delta_pct !== null ? "%" : ""}</span>;
}

function Tile({ id, k, slot, options, onPick }) {
  const L = LOOK[id] || LOOK.active_jobs;
  return (
    <div className="relative bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-5 flex gap-4 hover:shadow-md transition-shadow" data-testid={`kpi-tile-${slot}`}>
      <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${L.tone}`}><L.Icon className="w-5 h-5 sm:w-6 sm:h-6" /></div>
      <Link to={k.to || "/"} className="min-w-0 flex-1" data-testid={`kpi-link-${id}`}>
        <p className="text-sm text-slate-600 truncate pr-6">{k.label}</p>
        <div className="flex items-end justify-between gap-2">
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums mt-1" data-testid={`kpi-value-${id}`}>{fmt(k, k.value)}</p>
          {k.trend ? <Spark vals={k.trend} /> : <Delta k={k} />}
        </div>
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 truncate">{k.trend && <Delta k={k} />}{k.vs && (k.delta_pct ?? k.delta) != null ? k.vs : k.sub}{k.sub && k.vs && (k.delta_pct ?? k.delta) != null ? ` · ${k.sub}` : ""}</p>
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Change figure" className="absolute right-2.5 top-2.5 w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center" data-testid={`kpi-menu-${slot}`}><MoreHorizontal className="w-4 h-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Show in this slot</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map(([key, o]) => (
            <DropdownMenuItem key={key} disabled={key === id} onSelect={() => onPick(slot, key)} data-testid={`kpi-pick-${slot}-${key}`}>{o.label}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {id === "priority" && <ChevronRight className="absolute right-3 bottom-4 w-4 h-4 text-slate-300" />}
    </div>
  );
}

export function KpiTiles({ data, chosen, onChange }) {
  if (!data) { return <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-[112px] rounded-2xl bg-slate-100 animate-pulse" />)}</div>; }
  const all = data.kpis || {};
  const keys = (chosen && chosen.length ? chosen : data.default).filter((k) => all[k]);
  const filled = [...keys, ...data.keys.filter((k) => !keys.includes(k))].slice(0, 4);
  const options = data.keys.map((k) => [k, all[k]]);
  const pick = (slot, key) => {
    const next = [...filled];
    const other = next.indexOf(key);
    if (other >= 0) { next[other] = next[slot]; }
    next[slot] = key;
    onChange(next);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kpi-tiles">
      {filled.map((k, i) => <Tile key={k} id={k} k={all[k]} slot={i} options={options} onPick={pick} />)}
    </div>
  );
}
